import Button from 'ext-ui/components/Button/Button'
import React, { ReactElement } from 'react'
import { useTranslate } from 'src/localization/Trns'

import { PhysicalCardManagementStatus } from '../cardDetailHeroDemoData'
import SvgEye from '../icons/SvgEye'
import SvgEyeOff from '../icons/SvgEyeOff'
import SvgSnowflake from '../icons/SvgSnowflake'
import * as style from './CardDisplayActions.style'

type CardDisplayActionsProps = {
  status: PhysicalCardManagementStatus
  /** Toggles the demo freeze (active ⇄ frozen); a no-op for issuer-imposed LOCKED. */
  onFreeze: () => void
  /** Reveal-disclosure state, owned by the hero shell. */
  revealOpen: boolean
  onToggleReveal: () => void
  /**
   * The reveal panel's DOM id, generated with `useId` by whichever variant owns
   * this cluster (the shell for `full`, the compact bar for `compact`) and shared
   * with its `CardRevealPanel`, so the Show details trigger's `aria-controls`
   * resolves to the right panel while both heroes are mounted.
   */
  revealPanelId: string
}

/**
 * The card-display action cluster in the hero toolbar: Show details (the reveal
 * disclosure trigger) then Freeze. The `•••` menu joins in slice 3.
 *
 * The freeze label is derived from status so the button copy agrees with the
 * summary pill and the card-art overlay — a single shell-owned status drives all
 * three, per the design spec's "one owner" rule.
 */
const CardDisplayActions: React.FC<CardDisplayActionsProps> = ({
  status,
  onFreeze,
  revealOpen,
  onToggleReveal,
  revealPanelId,
}): ReactElement => {
  const translate = useTranslate()

  const freezeLabel = ((): string => {
    switch (status) {
      case 'FROZEN':
        return translate('Unfreeze')
      case 'LOCKED':
        return translate('Request unlock')
      default:
        return translate('Freeze')
    }
  })()

  return (
    <div css={style.actions}>
      <Button
        variant="secondary"
        iconLeft={
          revealOpen ? (
            <SvgEyeOff width={15} height={15} />
          ) : (
            <SvgEye width={15} height={15} />
          )
        }
        onClick={onToggleReveal}
        aria-expanded={revealOpen}
        aria-controls={revealOpen ? revealPanelId : undefined}
        data-testid="card-show-details-button"
      >
        {revealOpen ? translate('Hide details') : translate('Show details')}
      </Button>
      <Button
        variant="secondary"
        iconLeft={<SvgSnowflake width={15} height={15} />}
        onClick={onFreeze}
        data-testid="card-freeze-button"
      >
        {freezeLabel}
      </Button>
    </div>
  )
}

export default CardDisplayActions
